/**
 * Determines the shoppability status based on website and country configurations.
 *
 * This composable checks if the current website and country are enabled for shopping.
 * It provides computed properties to determine if a brand or country is shoppable.
 *
 * @returns {Object} An object containing shoppability status flags
 * @property {ComputedRef<boolean>} isCountryShoppable - Indicates if the current country is shoppable
 * @property {ComputedRef<boolean>} isBrandShoppable - Indicates if the current brand is shoppable
 * @property {ComputedRef<boolean>} isShoppable - Indicates if both brand and country are shoppable
 *
 * @example
 * const { isShoppable, isCountryShoppable, isBrandShoppable } = useIsShoppable()
 * console.log(isShoppable.value) // true or false
 */
export const useIsShoppable = () => {
  const configs = useConfigs()
  const { currentWebsite } = useWebsite()

  const { shopEnabled } = useBrandsSettings()
  const isCountryShoppable = computed(() => configs.value.is_shoppable ?? false)
  const isBrandShoppable = computed(
    () => shopEnabled(currentWebsite.value) ?? false
  )

  const isShoppable = computed(
    () => (isBrandShoppable.value && isCountryShoppable.value) ?? false
  )

  return {
    isCountryShoppable,
    isBrandShoppable,
    isShoppable,
  }
}
